import { Control, useFormContext, useWatch } from 'react-hook-form';
import { Box, MenuItem, Typography, useTheme } from '@mui/material';

import Flex from '@Components/Bases/Flex';
import { FormDatePicker, FormInput } from '@Components/Form';
import FormSelect from '@Components/Form/FormSelect';
import { durationOptions, formatMonthYearEn } from './utils';
import { DurationType } from 'src/Enum/GoalsAndOperationalPlans';
import {
  disabledDatePickerTypingStyles,
  disabledFormInputStyles,
} from '@Components/Form/styles';
import { IGoalsAndOperationalPlansFormValues } from '@Router/GoalAndOperationalPlans/Containers/GoalAndOperationalPlansFormProvider/types';
import dayjs from 'dayjs';

type GoalDurationFormProps = {
  goalsOperationalPlansFormControl: Control<
    IGoalsAndOperationalPlansFormValues,
    unknown
  >;
};

const GoalDurationForm = ({
  goalsOperationalPlansFormControl,
}: GoalDurationFormProps) => {
  const theme = useTheme();
  const completedSection = useWatch({
    control: goalsOperationalPlansFormControl,
    name: 'completedSection',
  });

  const { isCompletedTh } = completedSection ?? {};

  const { watch, setValue, unregister } = useFormContext();

  const selectedDuration = watch('durationType');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const formattedDateEn = startDate
    ? `${formatMonthYearEn(startDate)} - ${formatMonthYearEn(endDate)}`
    : formatMonthYearEn(endDate);

  return (
    <Box
      border={`1px solid ${theme.palette.grey[200]}`}
      borderRadius={1}
      p={1}
      mt={2}
    >
      <Flex
        gap={1.5}
        alignItems={'baseline'}
        flexDirection={{ xs: 'column', md: 'row' }}
      >
        <FormSelect
          name="durationType"
          label={'ระยะเวลาเป้าหมาย (ไทย)'}
          placeholder="กรุณาเลือก"
          onChange={(event) => {
            const value = event.target.value as DurationType;
            setValue('durationType', value);
            unregister('startDate');
            unregister('endDate');
          }}
          required
          sx={isCompletedTh ? disabledFormInputStyles : undefined}
        >
          {durationOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </FormSelect>
        {selectedDuration === DurationType.MONTH_PERIOD && (
          <Flex width={'100%'} gap={0.5}>
            <FormDatePicker
              name="startDate"
              placeholder="กรุณาเลือกระยะเวลาเป้าหมาย"
              views={['year', 'month']}
              format="MMMM BBBB"
              sx={{
                minWidth: { md: 150 },
                width: { xs: '100%' },
                ...(isCompletedTh
                  ? disabledFormInputStyles
                  : disabledDatePickerTypingStyles),
              }}
              maxDate={endDate}
              minDate={dayjs()}
              slotProps={{ toolbar: { hidden: false } }}
              localeText={{ toolbarTitle: 'เลือก เดือน ปี' }}
            />
            <Typography pt={0.5}>{'\u2014'}</Typography>
            <FormDatePicker
              name="endDate"
              placeholder="กรุณาเลือกระยะเวลาเป้าหมาย"
              views={['year', 'month']}
              format="MMMM BBBB"
              sx={{
                minWidth: { md: 150 },
                width: { xs: '100%' },
                ...(isCompletedTh
                  ? disabledFormInputStyles
                  : disabledDatePickerTypingStyles),
              }}
              minDate={startDate || dayjs()}
              slotProps={{ toolbar: { hidden: false } }}
              localeText={{ toolbarTitle: 'เลือก เดือน ปี' }}
            />
          </Flex>
        )}
        {selectedDuration === DurationType.WITHIN_MONTH && (
          <FormDatePicker
            name="endDate"
            placeholder="กรุณาเลือกระยะเวลาเป้าหมาย"
            views={['year', 'month']}
            format="MMMM BBBB"
            sx={{
              width: '100%',
              ...(isCompletedTh
                ? disabledFormInputStyles
                : disabledDatePickerTypingStyles),
            }}
            minDate={dayjs()}
            slotProps={{ toolbar: { hidden: false } }}
            localeText={{ toolbarTitle: 'เลือก เดือน ปี' }}
          />
        )}
        <FormInput
          name=""
          label="ระยะเวลาเป้าหมาย (อังกฤษ)"
          placeholder={'กรุณาเลือกระยะเวลา (อังกฤษ)'}
          sx={disabledFormInputStyles}
          fullWidth
          required
          value={endDate ? formattedDateEn : ''}
        />
      </Flex>
    </Box>
  );
};

export default GoalDurationForm;
